import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import vert from "./shaders/player.vert.glsl";
import frag from "./shaders/drone.frag.glsl";
import { type Cloud } from "./formations";
import { createSwarm, retarget, stepSwarm, type WaveDrive } from "./engine/swarm";
import { audioBus } from "./audioBus";
import { DEFAULT_FX, type AudioFX } from "./audioFx";
import { PLATE_L } from "./source";
import type { CymDrive } from "./DroneField";

/**
 * LIGHT UNITS — the legible scale for the physics dials.
 * 1 drone ≈ 1 light unit (lu) ≈ 0.1 m, and 1 lu = 0.08 scene units.
 * So: safe zone 10 lu ≈ 1 m apart; speed 10 lu/s ≈ 1 m/s (100 lu/s = 10 m/s,
 * a real drone transit speed). Dials store lu; the binding converts for the
 * engine (which is unit-agnostic).
 */
export const LIGHT_UNIT = 0.08; // scene units per light unit
export const METERS_PER_LU = 0.1;
export const METER = LIGHT_UNIT / METERS_PER_LU; // scene units per metre (0.8)

export type PhysicsParams = {
  safeZone: number; // min comfortable center-to-center distance (light units)
  maxSpeed: number; // light units / second
  rigidity: number; // seek-to-slot spring strength
  avoidance: number; // separation force strength
  damping: number; // velocity damping
};

export const DEFAULT_PHYSICS: PhysicsParams = {
  safeZone: 1.0, // ≈ 10 cm — one drone apart
  maxSpeed: 10, // ≈ 1 m/s — calm, legible motion by default
  rigidity: 6,
  avoidance: 28,
  damping: 3.5,
};

/**
 * Neighbor physics, as a THIN BINDING over the pure engine
 * (engine/swarm.ts): this component owns rendering (geometry, shader
 * uniforms), unit conversion (lu -> scene), the eased Size space, and the
 * mapping from the audio-FX rack + audioBus onto the engine's WaveDrive.
 * The seek/separation/integration physics, the untangle, the spatial hash
 * and the wave-field radius modulation all live in the engine.
 */
export function NeighborField({
  cloud,
  params,
  brightness = 1.5,
  size = 6.0,
  scale = 1,
  cym = null,
  fx = DEFAULT_FX,
  flow = null,
}: {
  cloud: Cloud;
  params: PhysicsParams;
  brightness?: number;
  size?: number;
  scale?: number;
  cym?: CymDrive | null;
  fx?: AudioFX;
  /** Curve flow: drones stream along the (ordered) source curve. speed = laps/min. */
  flow?: { speed: number } | null;
}) {
  const matRef = useRef<THREE.ShaderMaterial>(null);
  const mountCloud = useRef(cloud);
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const scaleTarget = useRef(scale);
  scaleTarget.current = scale;
  const curScale = useRef(scale);
  const cymRef = useRef(cym);
  cymRef.current = cym;
  const curCym = useRef<{ m: number; n: number } | null>(null);
  const fxRef = useRef(fx);
  fxRef.current = fx;
  const hueAngle = useRef(0);
  const ptsRef = useRef<THREE.Points>(null);
  const flowRef = useRef(flow);
  flowRef.current = flow;

  const sim = useMemo(
    () =>
      createSwarm(
        mountCloud.current.positions,
        mountCloud.current.colors,
        (Math.random() * 0x100000000) >>> 0 // fresh look per mount; tests inject fixed seeds
      ),
    []
  );

  const geometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(sim.pos, 3));
    g.setAttribute("aColor", new THREE.BufferAttribute(sim.col, 3));
    g.setAttribute("aSeed", new THREE.BufferAttribute(sim.seeds, 1));
    return g;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sim]);
  useEffect(() => () => geometry.dispose(), [geometry]);

  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uSize: { value: size },
      uBrightness: { value: brightness },
      uAudioLevel: { value: 0 },
      uAudioBeat: { value: 0 },
      uShimmer: { value: 0 },
      uHueShift: { value: 0 },
      uCymAmp: { value: 0 },
      uCymM: { value: 0 },
      uCymN: { value: 0 },
      uCymMix: { value: -1 },
      uCymL: { value: PLATE_L },
      uCymSpeed: { value: 2.4 },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(() => {
    if (!matRef.current) return;
    matRef.current.uniforms.uSize.value = size;
    matRef.current.uniforms.uBrightness.value = brightness;
  }, [size, brightness]);

  // retarget on cloud change: fly to the new formation (physics avoids collisions)
  const lastCloud = useRef(cloud);
  useEffect(() => {
    if (cloud === lastCloud.current) return;
    lastCloud.current = cloud;
    retarget(sim, cloud.positions, cloud.colors);
  }, [cloud, sim]);

  useFrame((state, dtRaw) => {
    const m = matRef.current;
    if (!m) return;
    m.uniforms.uTime.value = state.clock.elapsedTime;

    const p = paramsRef.current;
    const dt = Math.min(dtRaw, 1 / 30);
    const av = audioBus.values;
    const fxc = fxRef.current;

    // ---- the audio-FX rack: shader-side units (Pulse / Shimmer / Hue / Spin) ----
    m.uniforms.uAudioLevel.value = fxc.pulse.on ? av.level * fxc.pulse.amt : 0;
    m.uniforms.uAudioBeat.value = fxc.pulse.on ? av.beat * fxc.pulse.amt : 0;
    m.uniforms.uShimmer.value = fxc.shimmer.on ? av.treble * fxc.shimmer.amt : 0;
    if (fxc.hue.on) hueAngle.current += av.mid * fxc.hue.amt * dt * 0.6;
    m.uniforms.uHueShift.value = fxc.hue.on ? hueAngle.current : 0;
    // bass torques the whole swarm (a render transform — physics untouched)
    if (fxc.spin.on && ptsRef.current) ptsRef.current.rotation.y += av.bass * av.bass * 1.2 * fxc.spin.amt * dt;

    // cymatics plate: the standing wave pumps in neighbor physics too — the
    // mode numbers EASE toward their target, so a sweep melts figure into
    // figure (non-integer plates are valid Chladni fields; see drone.vert)
    const cy = cymRef.current;
    m.uniforms.uCymAmp.value = cy ? cy.amp * (1 + av.bass * 1.6 + av.beat * 0.5) : 0;
    if (cy) {
      if (!curCym.current) curCym.current = { m: cy.m, n: cy.n }; // snap on entry
      const ck = Math.min(1, dt * 2.5);
      curCym.current.m += (cy.m - curCym.current.m) * ck;
      curCym.current.n += (cy.n - curCym.current.n) * ck;
      m.uniforms.uCymM.value = curCym.current.m;
      m.uniforms.uCymN.value = curCym.current.n;
      m.uniforms.uCymMix.value = cy.mix;
      m.uniforms.uCymSpeed.value = cy.speed;
    } else {
      curCym.current = null;
    }

    // Size scales the SWARM SPACE, not the render: the seek targets spread out
    // (eased), so a larger Size gives the drones genuine room — at high count
    // they stop crowding instead of the whole picture just zooming in.
    // The Bass-swell effect breathes the target space; the physics chases it.
    curScale.current += (scaleTarget.current - curScale.current) * Math.min(1, dt * 8);
    const sc = curScale.current * (1 + (fxc.swell.on ? av.bass * 0.1 * fxc.swell.amt : 0));

    // the physics-side rack units map onto the engine's wave drive
    const wave: WaveDrive | null =
      fxc.push.on || fxc.waves.on || fxc.ripples.on
        ? {
            level: av.level,
            bass: av.bass,
            beat: av.beat,
            push: fxc.push.on ? { frac: fxc.push.frac, amt: fxc.push.amt } : null,
            waves: fxc.waves.on ? { amt: fxc.waves.amt } : null,
            ripples: fxc.ripples.on ? { amt: fxc.ripples.amt } : null,
          }
        : null;

    stepSwarm(sim, dt, {
      params: {
        safe: p.safeZone * LIGHT_UNIT, // dial units (lu) -> scene units
        maxSpeed: p.maxSpeed * LIGHT_UNIT,
        rigidity: p.rigidity,
        avoidance: p.avoidance,
        damping: p.damping,
      },
      scale: sc,
      wave,
      flow: flowRef.current,
    });

    (geometry.getAttribute("position") as THREE.BufferAttribute).needsUpdate = true;
    (geometry.getAttribute("aColor") as THREE.BufferAttribute).needsUpdate = true;
  });

  return (
    <points ref={ptsRef} name="swarm" geometry={geometry} frustumCulled={false}>
      <shaderMaterial
        ref={matRef}
        vertexShader={vert}
        fragmentShader={frag}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        depthTest={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}
